import api from '../../api/api';
import { previewActivity } from './previewActivity';

export const previewKey = (track) => `${track.source || track.albumSource}:${track.sourceId}`;
export const validPreviewUrl = (raw) => {
  try {
    const url = new URL(raw);
    return url.protocol === 'https:' && !url.username && !url.password ? url.href : null;
  } catch {
    return null;
  }
};
export function previewTrack(track, album = {}) {
  return {
    ...track,
    source: track.source || track.albumSource || album.albumSource,
    artistName: track.artistName || album.artist?.name || '',
    artworkUrl: track.artworkUrl || track.albumImageUrl || album.imageUrl,
    albumId: track.albumId || album.id,
    albumSlug: track.albumSlug || album.slug,
    albumSourceId: track.albumSourceId || album.sourceId,
  };
}
export function canPreview(track) {
  const source = track?.source || track?.albumSource;
  return Boolean(
    track?.sourceId &&
      (source === 'APPLE_MUSIC' || (source === 'POSTPOP' && validPreviewUrl(track.previewUrl))),
  );
}
export function previewQueue(tracks) {
  const seen = new Set();
  return tracks.filter((track) => {
    if (!canPreview(track) || seen.has(previewKey(track))) return false;
    seen.add(previewKey(track));
    return true;
  });
}

const EMPTY = { track: null, queue: [], index: -1, owner: null, status: 'idle', error: null };
const EMPTY_PROGRESS = { current: 0, duration: 0 };

// Injected transport/audio make races and disposal testable without playing sound.
export function createPreviewPlayer({
  createAudio = () => new Audio(),
  resolve = async (track, signal) => {
    const response = await api.get(`/search/tracks/${encodeURIComponent(track.sourceId)}/preview`, {
      signal,
      requireAuth: false,
      suppressGlobalError: true,
    });
    return response.data.data?.previewUrl;
  },
} = {}) {
  let state = EMPTY;
  let progress = EMPTY_PROGRESS;
  let audio;
  let generation = 0;
  let abort;
  let deadline;
  let cleanAudio = () => {};
  const listeners = new Set();
  const progressListeners = new Set();
  const cache = new Map();
  const suppressedOwners = new Set();
  const emit = (patch) => {
    state = { ...state, ...patch };
    state.miniSuppressed = Boolean(state.owner && suppressedOwners.has(state.owner));
    previewActivity.setVisible(Boolean(state.track) && !state.miniSuppressed);
    previewActivity.set(['loading', 'playing'].includes(state.status));
    listeners.forEach((listener) => listener());
  };
  const updateProgress = (value) => {
    progress = value;
    progressListeners.forEach((listener) => listener());
  };
  const release = () => {
    clearTimeout(deadline);
    deadline = undefined;
    abort?.abort();
    abort = undefined;
    cleanAudio();
    cleanAudio = () => {};
    if (audio) {
      audio.pause();
      audio.removeAttribute('src');
      audio.load();
      audio = undefined;
    }
  };
  const fail = (error) => {
    generation += 1;
    release();
    emit({ status: 'error', error });
  };
  const pause = () => {
    if (!state.track || !['loading', 'playing'].includes(state.status)) return;
    if (state.status === 'loading') {
      generation += 1;
      release();
    } else {
      clearTimeout(deadline);
      audio?.pause();
    }
    emit({ status: 'paused', error: null });
  };
  async function loadAudio(token, track, url, key, queue, next, owner) {
    emit({ track: { ...track, previewUrl: url }, index: next });
    if (document.hidden) {
      pause();
      return false;
    }
    const element = createAudio();
    audio = element;
    element.preload = 'none';
    const current = () => token === generation && audio === element;
    const events = {
      playing: () => {
        if (current()) {
          clearTimeout(deadline);
          emit({ status: 'playing', error: null });
        }
      },
      waiting: () => {
        if (!current() || state.status === 'paused') return;
        emit({ status: 'loading' });
        clearTimeout(deadline);
        deadline = setTimeout(() => {
          if (current()) fail('previewError');
        }, 15000);
      },
      pause: () => {
        if (current() && !element.ended) emit({ status: 'paused' });
      },
      timeupdate: () => {
        if (current())
          updateProgress({
            current: element.currentTime || 0,
            duration: Number.isFinite(element.duration) ? element.duration : 0,
          });
      },
      durationchange: () => {
        if (current()) events.timeupdate();
      },
      error: () => {
        if (current()) {
          cache.delete(key);
          fail('previewError');
        }
      },
      ended: () => {
        if (!current() || document.hidden || state.status === 'paused') return;
        if (next + 1 < queue.length) void start(queue, next + 1, owner);
        else {
          clearTimeout(deadline);
          emit({ status: 'ended' });
        }
      },
    };
    Object.entries(events).forEach(([event, handler]) => element.addEventListener(event, handler));
    cleanAudio = () =>
      Object.entries(events).forEach(([event, handler]) =>
        element.removeEventListener(event, handler),
      );
    element.src = url;
    // Always reached from a requested play/queue. A blocked play remains explicit.
    await element.play();
    return current();
  }
  async function start(queue, index, owner) {
    const token = ++generation;
    release();
    updateProgress(EMPTY_PROGRESS);
    emit({ queue, index, owner, track: queue[index], status: 'loading', error: null });
    abort = new AbortController();
    const signal = abort.signal;
    deadline = setTimeout(() => {
      if (generation === token) fail('previewError');
    }, 15000);
    try {
      for (let next = index; next < queue.length; next += 1) {
        const track = queue[next];
        const key = previewKey(track);
        const cached = cache.get(key);
        let raw = track.previewUrl || (cached?.expires > Date.now() ? cached.url : null);
        if (!raw && track.source === 'APPLE_MUSIC') {
          try {
            raw = await resolve(track, signal);
          } catch {
            if (token !== generation) return false;
            continue;
          }
        }
        if (token !== generation) return false;
        const url = validPreviewUrl(raw);
        if (!url) continue;
        cache.set(key, { url, expires: Date.now() + 30 * 60 * 1000 });
        if (cache.size > 100) cache.delete(cache.keys().next().value);
        return await loadAudio(token, track, url, key, queue, next, owner);
      }
      if (token === generation) fail('previewUnavailable');
    } catch (error) {
      if (token === generation)
        fail(error.name === 'NotAllowedError' ? 'previewBlocked' : 'previewError');
    }
    return false;
  }
  const dismiss = () => {
    generation += 1;
    release();
    updateProgress(EMPTY_PROGRESS);
    emit(EMPTY);
  };
  return {
    getSnapshot: () => state,
    subscribe: (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    getProgress: () => progress,
    subscribeProgress: (listener) => {
      progressListeners.add(listener);
      return () => progressListeners.delete(listener);
    },
    play(track, tracks = [track], owner = null) {
      const queue = previewQueue(tracks.map((item) => previewTrack(item)));
      const index = queue.findIndex((item) => previewKey(item) === previewKey(track));
      if (index < 0) return Promise.resolve(false);
      return start(queue, index, owner);
    },
    next() {
      if (state.index + 1 < state.queue.length)
        void start(state.queue, state.index + 1, state.owner);
    },
    toggle(owner = state.owner) {
      if (!state.track) return;
      if (owner !== state.owner) emit({ owner });
      if (['playing', 'loading'].includes(state.status)) {
        pause();
        return;
      }
      if (!audio || state.status === 'error') {
        void start(state.queue, state.index, state.owner);
        return;
      }
      const token = generation;
      if (state.status === 'ended') audio.currentTime = 0;
      emit({ status: 'loading', error: null });
      deadline = setTimeout(() => {
        if (token === generation) fail('previewError');
      }, 15000);
      void audio.play().catch((error) => {
        if (token === generation)
          fail(error.name === 'NotAllowedError' ? 'previewBlocked' : 'previewError');
      });
    },
    pause,
    dismiss,
    setMiniPlayerSuppressed(owner, suppressed) {
      if (suppressed) suppressedOwners.add(owner);
      else suppressedOwners.delete(owner);
      emit({});
    },
    dismissOwned(owner) {
      if (state.owner === owner) dismiss();
    },
    attach() {
      const visibility = () => {
        if (document.hidden) pause();
      };
      const pagehide = () => pause();
      let focusTimer;
      const blur = () => {
        focusTimer = setTimeout(() => {
          if (document.activeElement?.tagName === 'IFRAME') pause();
        }, 0);
      };
      document.addEventListener('visibilitychange', visibility);
      window.addEventListener('pagehide', pagehide);
      window.addEventListener('blur', blur);
      return () => {
        document.removeEventListener('visibilitychange', visibility);
        window.removeEventListener('pagehide', pagehide);
        window.removeEventListener('blur', blur);
        clearTimeout(focusTimer);
        dismiss();
      };
    },
  };
}
export const webPreviewPlayer = createPreviewPlayer();
